import type { ReactNode } from "react";
import React from "react";
import { AuthAdapter } from "../../api/auth/AuthAdapter";
import { AppConfigContext } from "../app/appConfig";
import { AuthModel } from "../../api/auth/AuthModel";
import { UserModel } from "../../api/users/UserModel";
import { UserAdapter } from "../../api/users/UserAdapter";
import type {
  LoginForm,
  User,
  LoginResponse,
  CompanyRole,
  UserWithRole,
} from "../../api/auth/Auth.typed";
import type { Role } from "../../api/roles/Role.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";
import type {
  UpdateUserProfilePayload,
  CreateDirectoryUserPayload,
  ActivateUserAccountPayload,
  UserDocument,
  UserDocumentPatchPayload,
  UserDocumentsBatchItem,
  UserDocumentsAlertBatchItem,
  UserDocumentsAlertBatchResponseItem,
  UserDisplayBatchItem,
} from "../../api/users/User.typed";
import { useAlert } from "../app/notificationToast";
import { useNavigate } from "react-router-dom";
import { userDisplayName } from "../../utils/userHelper";

type UserWithCompany = User & { Role?: { company_id?: number } };

function syntheticRoleFromCompanies(companies: CompanyRole[]): Role | undefined {
  const allPerms = new Set<string>();
  let anyEmployee = false;
  for (let i = 0; i < companies.length; i++) {
    const pl = companies[i].role?.permission_list;
    if (Array.isArray(pl)) {
      for (let j = 0; j < pl.length; j++) {
        allPerms.add(pl[j]);
      }
    }
    if (companies[i].role?.is_employee) {
      anyEmployee = true;
    }
  }
  if (allPerms.size === 0 && !anyEmployee) return undefined;
  return {
    id: 0,
    name: "",
    permission_list: Array.from(allPerms) as Role["permission_list"],
    is_employee: anyEmployee,
  };
}

function userFromLoginResponse(lr: LoginResponse, previous: User): User {
  const companies = lr.Companies ?? [];
  const baseUser = { ...lr.User };
  const companyId = previous.company_id;

  if (companyId) {
    const match = companies.find((c) => c.company.id === companyId);
    if (match?.role) {
      return {
        ...baseUser,
        company_id: companyId,
        Role: { ...match.role, company_id: companyId },
      };
    }
  }

  if (baseUser.Role) {
    return {
      ...baseUser,
      company_id: companyId ?? baseUser.company_id,
      Role: {
        ...baseUser.Role,
        company_id: companyId ?? baseUser.company_id ?? baseUser.Role.company_id,
      },
    };
  }

  if (companies.length === 1 && companies[0].role) {
    const cid = companies[0].company.id;
    return {
      ...baseUser,
      company_id: cid,
      Role: { ...companies[0].role, company_id: cid },
    };
  }

  if (companies.length >= 2) {
    const synth = syntheticRoleFromCompanies(companies);
    if (synth) {
      return {
        ...baseUser,
        company_id: companyId,
        Role: synth,
      };
    }
  }

  return {
    ...baseUser,
    company_id: companyId ?? baseUser.company_id,
    Role: baseUser.Role ?? previous.Role,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthContextType {
  authClient: AuthAdapter;
  userClient: UserAdapter;
  loginCallback(form: LoginForm): Promise<void>;
  logoutCallback(): Promise<void>;
  isLogged: boolean;
  user: User | undefined;
  companyOptions?: CompanyRole[];
  isCompanyModalOpen: boolean;
  selectCompany: (companyId: number) => Promise<void>;
  dismissCompanyModal: () => void;
  userCompanies: CompanyRole[];
  openCompanySwitchModal: () => void;
  resetRequest(form: LoginForm): Promise<void>;
  resetPassword(form: LoginForm): Promise<void>;
  /** `employeesOnly` filters server-side on the tenant role, so it works without users.roles.view. */
  getUsers(employeesOnly?: boolean): Promise<UserWithRole[] | undefined>;
  getUsersDisplayBatch(ids: number[]): Promise<UserDisplayBatchItem[] | undefined>;
  userNameMap: Record<number, string>;
  getUserName: (id: number) => string | undefined;
  refreshUsersMap: (prefetched?: UserWithRole[]) => Promise<void>;
  createDirectoryUser(payload: CreateDirectoryUserPayload): Promise<void>;
  activateUserAccount(id: number, payload: ActivateUserAccountPayload): Promise<void>;
  resendUserInvitation(id: number): Promise<void>;
  createInvitedUser(form: LoginForm): Promise<void>;
  verifyToken(form: LoginForm): Promise<boolean | undefined>;
  deleteUser(id: number): Promise<void>;
  updateUserRole(id: number, role: number): Promise<void>;
  updateUserProfile(id: number, payload: UpdateUserProfilePayload): Promise<void>;
  getUserDocuments(
    userId: number,
    params?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<UserDocument> | undefined>;
  getUserDocumentsBatch(
    items: UserDocumentsBatchItem[],
  ): Promise<PaginatedResponse<UserDocument>[] | undefined>;
  getUserDocumentsAlertBatch(
    items: UserDocumentsAlertBatchItem[],
  ): Promise<UserDocumentsAlertBatchResponseItem[] | undefined>;
  uploadUserDocument(userId: number, formData: FormData): Promise<UserDocument | undefined>;
  patchUserDocument(
    userId: number,
    documentId: number,
    payload: UserDocumentPatchPayload,
  ): Promise<UserDocument | undefined>;
  deleteUserDocument(userId: number, documentId: number): Promise<void>;
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export function AuthProvider(props: AuthProviderProps) {
  const appConfig = React.useContext(AppConfigContext);

  const authModel = React.useMemo(() => new AuthModel(appConfig!), [appConfig]);
  const userModel = React.useMemo(() => new UserModel(appConfig!), [appConfig]);
  const userClient = React.useMemo(() => new UserAdapter(userModel), [userModel]);
  const authClient = React.useMemo(() => new AuthAdapter(authModel), [authModel]);

  const intervalMe = React.useRef<NodeJS.Timeout>();
  const companyOptionsRef = React.useRef<CompanyRole[]>([]);

  const [init] = React.useState(() => {
    try {
      const raw = localStorage.getItem("userSession");
      const user: User | undefined = raw
        ? (() => {
            const p = JSON.parse(raw) as Record<string, unknown>;
            const u = p as unknown as User;
            return u.id > 0 ? u : undefined;
          })()
        : undefined;
      const userCompanies: CompanyRole[] = (() => {
        try {
          const r = localStorage.getItem("userCompanies");
          return r ? JSON.parse(r) : [];
        } catch {
          return [];
        }
      })();
      return { user, sessionReady: !raw, userCompanies };
    } catch {
      return {
        user: undefined as User | undefined,
        sessionReady: true,
        userCompanies: [] as CompanyRole[],
      };
    }
  });

  const [user, setUser] = React.useState<User | undefined>(init.user);
  const isLogged = !!(
    user &&
    ((user as UserWithCompany).company_id ?? (user as UserWithCompany).Role?.company_id)
  );
  const [companyOptions, setCompanyOptions] = React.useState<CompanyRole[]>();
  const [isCompanyModalOpen, setCompanyModalOpen] = React.useState<boolean>(false);
  const [userCompanies, setUserCompanies] = React.useState<CompanyRole[]>(init.userCompanies);
  const [userNameMap, setUserNameMap] = React.useState<Record<number, string>>({});
  const [sessionReady, setSessionReady] = React.useState<boolean>(init.sessionReady);

  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const loginCallback = async (form: LoginForm) => {
    try {
      const res = await authClient.login(form);
      const hasCompanies =
        res != null && "Companies" in res && Array.isArray((res as LoginResponse).Companies);
      const companies = hasCompanies ? ((res as LoginResponse).Companies as CompanyRole[]) : [];

      if (companies.length === 1) {
        setUserCompanies(companies);
        localStorage.setItem("userCompanies", JSON.stringify(companies));
        await selectCompany(companies[0].company.id);
        return;
      }

      if (companies.length >= 2) {
        const lr = res as LoginResponse;
        setCompanyOptions(companies);
        setUserCompanies(companies);
        companyOptionsRef.current = companies;
        localStorage.setItem("userCompanies", JSON.stringify(companies));
        setCompanyModalOpen(true);
        if (lr.User) {
          const synth = syntheticRoleFromCompanies(companies);
          setUser({
            ...lr.User,
            ...(synth ? { Role: synth } : {}),
          });
        }
        return;
      }

      const u = res as User;
      localStorage.setItem("userSession", JSON.stringify(u));
      setUser(u);
      navigate("/");
    } catch (err) {
      if (err instanceof Error) showAlert(err.message, "danger");
    }
  };

  const selectCompany = async (companyId: number): Promise<void> => {
    try {
      const previousCompanyID = user?.company_id ?? user?.Role?.company_id;
      const u = await authClient.selectCompany(companyId);
      const userWithCompany: User = {
        ...u,
        Role: u.Role
          ? { ...u.Role, company_id: companyId }
          : {
              id: 0,
              name: "",
              permission_list: [],
              is_employee: false,
              company_id: companyId,
            },
        company_id: companyId,
      };
      localStorage.setItem("userSession", JSON.stringify(userWithCompany));
      setUser(userWithCompany);
      if (companyOptionsRef.current.length > 0) {
        setUserCompanies([...companyOptionsRef.current]);
        localStorage.setItem("userCompanies", JSON.stringify(companyOptionsRef.current));
      }
      companyOptionsRef.current = [];
      setCompanyOptions(undefined);
      setCompanyModalOpen(false);
      if (previousCompanyID != null && previousCompanyID !== companyId) {
        window.location.assign("/");
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof Error) showAlert(err.message, "danger");
    }
  };

  const openCompanySwitchModal = (): void => {
    if ((userCompanies || []).length < 2) return;
    companyOptionsRef.current = userCompanies;
    setCompanyOptions(userCompanies);
    setCompanyModalOpen(true);
  };

  const dismissCompanyModal = () => {
    setCompanyModalOpen(false);
  };

  const logoutCallback = async () => {
    try {
      await authClient.logout().then(() => {
        localStorage.removeItem("userSession");
        localStorage.removeItem("userCompanies");
        setUser(undefined);
        setUserCompanies([]);
        navigate("/login");
      });
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const resetPassword = async (form: LoginForm) => {
    try {
      await authClient
        .resetPassword(form)
        .then(() => showAlert("Mot de passe modifié !", "success"));
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const resetRequest = async (form: LoginForm) => {
    try {
      await authClient.resetRequest(form).then(() => showAlert("Demande reçue !", "success"));
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const getUsers = React.useCallback(
    async (employeesOnly = false): Promise<UserWithRole[] | undefined> => {
      try {
        return await userClient.getUsers(employeesOnly);
      } catch (err) {
        if (err instanceof Error) {
          showAlert(err.message, "danger");
        }
      }
    },
    [userClient, showAlert],
  );

  const USERS_DISPLAY_BATCH_LIMIT = 100;

  const getUsersDisplayBatch = React.useCallback(
    async (ids: number[]): Promise<UserDisplayBatchItem[] | undefined> => {
      try {
        if (ids.length === 0) return [];
        const dedup = Array.from(new Set(ids.filter((n) => Number.isFinite(n) && n > 0)));
        const out: UserDisplayBatchItem[] = [];
        for (let i = 0; i < dedup.length; i += USERS_DISPLAY_BATCH_LIMIT) {
          const slice = dedup.slice(i, i + USERS_DISPLAY_BATCH_LIMIT);
          const part = await userClient.getUsersDisplayBatch(slice);
          out.push(...part);
        }
        return out;
      } catch (err) {
        console.warn("[auth] getUsersDisplayBatch failed", err);
        return undefined;
      }
    },
    [userClient],
  );

  const refreshUsersMap = React.useCallback(
    async (prefetched?: UserWithRole[]): Promise<void> => {
      try {
        const list = prefetched ?? (await userClient.getUsers());
        if (Array.isArray(list)) {
          const map: Record<number, string> = {};
          list.forEach((u) => {
            map[u.user.id] = userDisplayName(u.user);
          });
          setUserNameMap(map);
        }
      } catch (err) {
        if (err instanceof Error && err.message) {
          showAlert(err.message, "danger");
        }
      }
    },
    [userClient, showAlert],
  );

  const getUserName = (id: number): string | undefined => userNameMap[id];

  const createDirectoryUser = async (payload: CreateDirectoryUserPayload) => {
    try {
      await userClient.createDirectoryUser(payload);
      showAlert(payload.email ? "Invitation envoyée" : "Utilisateur créé", "success");
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const activateUserAccount = async (id: number, payload: ActivateUserAccountPayload) => {
    try {
      await userClient.prepareUserActivation(id, payload);
      showAlert("Invitation d'activation envoyée", "success");
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const resendUserInvitation = async (id: number) => {
    try {
      await userClient.sendUserInvitationMail(id);
      showAlert("Invitation renvoyée", "success");
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const createInvitedUser = async (form: LoginForm) => {
    try {
      await userClient.createInvitedUser(form);
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const verifyToken = async (form: LoginForm): Promise<boolean | undefined> => {
    try {
      return await authClient.verifyToken(form);
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
        return false;
      }
    }
  };

  const deleteUser = async (id: number): Promise<void> => {
    try {
      await userClient.deleteUser(id).then(() => showAlert("Utilisateur supprimé !", "success"));
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const updateUserRole = async (id: number, role: number): Promise<void> => {
    try {
      await userClient
        .updateUserRole(id, role)
        .then(() => showAlert("Rôle mis à jour !", "success"));
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const updateUserProfile = async (
    id: number,
    payload: UpdateUserProfilePayload,
  ): Promise<void> => {
    try {
      await userClient
        .updateUserProfile(id, payload)
        .then(() => showAlert("Profil mis à jour", "success"));
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const getUserDocuments = async (
    userId: number,
    params?: { page?: number; limit?: number },
  ): Promise<PaginatedResponse<UserDocument> | undefined> => {
    try {
      return await userClient.getUserDocuments(userId, params);
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const getUserDocumentsBatch = async (
    items: UserDocumentsBatchItem[],
  ): Promise<PaginatedResponse<UserDocument>[] | undefined> => {
    try {
      if (items.length === 0) return [];
      return await userClient.getUserDocumentsBatch(items);
    } catch (err) {
      console.warn("[auth] getUserDocumentsBatch failed", err);
      return undefined;
    }
  };

  const getUserDocumentsAlertBatch = async (
    items: UserDocumentsAlertBatchItem[],
  ): Promise<UserDocumentsAlertBatchResponseItem[] | undefined> => {
    try {
      if (items.length === 0) return [];
      return await userClient.getUserDocumentsAlertBatch(items);
    } catch (err) {
      console.warn("[auth] getUserDocumentsAlertBatch failed", err);
      return undefined;
    }
  };

  const uploadUserDocument = async (
    userId: number,
    formData: FormData,
  ): Promise<UserDocument | undefined> => {
    try {
      const doc = await userClient.uploadUserDocument(userId, formData);
      showAlert("Document ajouté", "success");
      return doc;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const patchUserDocument = async (
    userId: number,
    documentId: number,
    payload: UserDocumentPatchPayload,
  ): Promise<UserDocument | undefined> => {
    try {
      const doc = await userClient.patchUserDocument(userId, documentId, payload);
      showAlert("Document mis à jour", "success");
      return doc;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const deleteUserDocument = async (userId: number, documentId: number): Promise<void> => {
    try {
      await userClient.deleteUserDocument(userId, documentId);
      showAlert("Document supprimé", "success");
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  React.useEffect(() => {
    if (intervalMe !== undefined) {
      clearInterval(intervalMe.current);
    }

    const meCallback = async (): Promise<void> => {
      try {
        const lr = await authClient.me();
        setUser((prev) => {
          if (!prev?.id) return prev;
          const next = userFromLoginResponse(lr, prev);
          localStorage.setItem("userSession", JSON.stringify(next));
          return next;
        });
      } catch (err) {
        localStorage.removeItem("userSession");
        setUser(undefined);
        setSessionReady(true);
        navigate("/login");
        showAlert("Vous avez été déconnecté", "warning");
        return;
      }
    };

    if (!user?.id) {
      setSessionReady(true);
      return;
    }

    const rawCompanies = localStorage.getItem("userCompanies");
    if (rawCompanies) {
      try {
        setUserCompanies(JSON.parse(rawCompanies));
      } catch {}
    }

    meCallback().then(() => setSessionReady(true));

    intervalMe.current = setInterval(() => {
      const u = localStorage.getItem("userSession");
      if (u) void meCallback();
    }, 60000);
    return () => {
      if (intervalMe !== undefined) {
        clearInterval(intervalMe.current);
      }
    };
  }, [authClient, navigate, showAlert, user?.id]);

  const contextValue = {
    authClient,
    userClient,
    loginCallback,
    logoutCallback,
    isLogged,
    user,
    companyOptions,
    isCompanyModalOpen,
    selectCompany,
    dismissCompanyModal,
    userCompanies: userCompanies ?? [],
    openCompanySwitchModal,
    resetPassword,
    resetRequest,
    getUsers,
    getUsersDisplayBatch,
    userNameMap,
    getUserName,
    refreshUsersMap,
    createDirectoryUser,
    activateUserAccount,
    resendUserInvitation,
    createInvitedUser,
    verifyToken,
    deleteUser,
    updateUserRole,
    updateUserProfile,
    getUserDocuments,
    getUserDocumentsBatch,
    getUserDocumentsAlertBatch,
    uploadUserDocument,
    patchUserDocument,
    deleteUserDocument,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {sessionReady ? props.children : null}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
